import DialogSelect from 'components/Dialog/DialogSelect.js';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter.js';
import MDBox from 'components/MDBox';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DashboardLayout from 'layouts/LayoutContainers/DashboardLayout';
import {
  Box,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDevicesUptime } from 'stores/adminCenter/adminCenterSlice';
import { setControllerTimeRangeValue } from 'stores/sensor/controllersSlice';
import { TimeInMS } from 'types/time';

const TimeRangeOptions = [
  { label: '1 Hour', value: TimeInMS.Hour },
  { label: 'Today', value: TimeInMS.Day },
  { label: '1 Week', value: TimeInMS.Week },
  { label: '1 Month', value: TimeInMS.Month },
  { label: 'date to date', value: 'date to date' },
];

const ACTIVE_SERVICE_THRESHOLD_SECONDS = 10 * 60;

const formatUnixTime = unixTime => {
  if (!unixTime) {
    return '-';
  }

  const date = new Date(Number(unixTime) * 1000);
  const pad = value => String(value).padStart(2, '0');

  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}, ${date.getDate()}/${
    date.getMonth() + 1
  }/${String(date.getFullYear()).slice(-2)}`;
};

const getTimeRangeMinutes = timeRange => {
  const timeRangeValue = timeRange?.value ?? timeRange;
  const timeRangeLabel = String(timeRange?.label || '').toLowerCase();

  if (timeRangeLabel === 'today' || timeRangeValue === TimeInMS.Day || timeRangeValue === 864000000) {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

    return Math.max((now.getTime() - startOfToday) / TimeInMS.Minute, 5);
  }

  if (typeof timeRangeValue === 'number') {
    return timeRangeValue / TimeInMS.Minute;
  }

  if (typeof timeRangeValue === 'string' && timeRangeValue.includes('_')) {
    const [startDate, endDate] = timeRangeValue.split('_');
    const startTime = new Date(`${startDate}T00:00:00`).getTime();
    const endTime = new Date(`${endDate}T00:00:00`).getTime() + TimeInMS.Day;

    if (Number.isFinite(startTime) && Number.isFinite(endTime) && endTime > startTime) {
      return (endTime - startTime) / TimeInMS.Minute;
    }
  }

  return TimeInMS.Day / TimeInMS.Minute;
};

const formatUptime = (recordsCount, timeRange) => {
  const expectedRecords = getTimeRangeMinutes(timeRange) / 5;

  if (!expectedRecords) {
    return '-';
  }

  const uptime = (Number(recordsCount || 0) / expectedRecords) * 100;
  return `${Math.round(Math.min(100, Math.max(0, uptime)))}%`;
};

const getServiceStatus = unixTime => {
  if (!unixTime) {
    return 'inactive';
  }

  const secondsSinceLastRecord = Math.floor(Date.now() / 1000) - Number(unixTime);
  return secondsSinceLastRecord <= ACTIVE_SERVICE_THRESHOLD_SECONDS ? 'active' : 'inactive';
};

const ServiceStatusBadge = ({ status }) => {
  const isOnline = status === 'active';
  const color = isOnline ? '#2f9d55' : '#b23a2f';
  const backgroundColor = isOnline ? '#dff7e7' : '#fde1dc';

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.55,
        borderRadius: '999px',
        backgroundColor,
        color,
        fontSize: 13,
        fontWeight: 800,
        lineHeight: 1,
        letterSpacing: 0,
        textTransform: 'uppercase',
      }}>
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: color,
          flex: '0 0 auto',
        }}
      />
      {isOnline ? 'ONLINE' : 'OFFLINE'}
    </Box>
  );
};

export const Statistics = () => {
  const dispatch = useDispatch();
  const rangeObj = useSelector(state => state.controllers.timeRange);
  const selectedFilterObject = useSelector(state => state.persistence?.selectedFilterObject || {});
  const { devicesUptime, devicesUptimeLoading, devicesUptimeError } = useSelector(state => state.adminCenter);

  const { distributor, customer, building, floor, area, hvacEquipment } = selectedFilterObject;
  const isBuildingSelected = building && building !== 'all';
  const tableColumnCount = isBuildingSelected ? 9 : 10;

  const devicesUptimeFilters = useMemo(() => {
    const normalizeFilter = value => {
      if (value === null || value === undefined || value === '') return 'all';
      return value;
    };

    return {
      distributor: normalizeFilter(distributor),
      customer: normalizeFilter(customer),
      building: normalizeFilter(building),
      floor: normalizeFilter(floor),
      area: normalizeFilter(area),
      hvacEquipment: normalizeFilter(hvacEquipment),
      timeRange: rangeObj?.value || TimeInMS.Day,
      userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }, [area, building, customer, distributor, floor, hvacEquipment, rangeObj?.value]);

  useEffect(() => {
    dispatch(fetchDevicesUptime(devicesUptimeFilters));
  }, [dispatch, devicesUptimeFilters]);

  const changeTimeRange = range => {
    dispatch(setControllerTimeRangeValue(range));
  };

  const emptyStateText = devicesUptimeError || 'No devices uptime data to display';

  return (
    <DashboardLayout>
      <DashboardNavbar objectName="Statistics" />
      <Typography
        fontSize="11px"
        sx={{
          paddingLeft: { xs: 0, sm: '12px' },
          marginTop: { xs: '-15px', sm: '-5px' },
          textAlign: { xs: 'center', sm: 'left' },
        }}>
        Smart indoor air monitoring & management platform
      </Typography>

      <MDBox
        pt={{ xs: 7, sm: 1 }}
        pb={1}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: {
            xs: 'calc(100dvh - 115px)',
            sm: 'calc(100dvh - 96px)',
          },
          minHeight: 0,
        }}>
        <Box sx={{ mb: 2, px: { xs: 0, sm: 1.5 }, textAlign: { xs: 'center', sm: 'left' } }}>
          <Typography sx={{ color: '#152536', fontSize: 28, fontWeight: 800, lineHeight: 1.2 }}>
            Statistics
          </Typography>
          <Typography sx={{ color: '#6d7178', fontSize: 15, fontWeight: 500, mt: 0.75 }}>
            Review device uptime, service status, and latest sensor or controller records for the selected operational scope.
          </Typography>
        </Box>
        <Grid container spacing={4} sx={{ flexShrink: 0 }}>
          <Grid item xs={12}>
            <MDBox
              mb={1}
              px={2}
              variant="gradient"
              borderRadius="lg"
              coloredShadow="light"
              id="topBar"
              sx={{
                position: 'relative',
                zIndex: 1200,
              }}
              style={{
                background: '#DEE4F9',
              }}>
              <Box
                sx={{
                  display: { xs: 'flex', sm: 'grid' },
                  gridTemplateColumns: { sm: '1fr', lg: 'minmax(0, 1fr) auto' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: { xs: 1, lg: 2 },
                }}>
                <Box sx={{ gridColumn: '1 / 2', width: '100%', minWidth: 0 }}>
                  <DevicesListFilter type="devices" status={false} search={false} hvacEquipment fullWidth />
                </Box>

                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                    justifyContent: { xs: 'center', lg: 'flex-end' },
                    gridColumn: { xs: '1 / -1', lg: '2 / 3' },
                    width: { xs: '100%', lg: 'auto' },
                    flexShrink: 0,
                  }}>
                  <DialogSelect
                    buttonTitle="Time Range"
                    rangeObj={rangeObj}
                    dialogTitle="Choose Time Range"
                    color="#000"
                    onSelectionChanged={changeTimeRange}
                    onSelectionOK={setControllerTimeRangeValue}
                    selectLabel="Time Range"
                    selectOptions={TimeRangeOptions}
                    default={TimeRangeOptions[1]}
                  />
                </Stack>
              </Box>
            </MDBox>
          </Grid>
        </Grid>
        <Box sx={{ mt: 1, flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <TableContainer
            component={Paper}
            sx={{
              height: '100%',
              minHeight: 0,
              overflow: 'auto',
            }}>
            <Table
              stickyHeader
              size="small"
              aria-label="Devices uptime"
              sx={{
                minWidth: 950,
                tableLayout: 'fixed',
              }}>
              <colgroup>
                <col style={{ width: '7%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '12%' }} />
                {!isBuildingSelected && <col style={{ width: '13%' }} />}
                <col style={{ width: '10%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell>Device ID</TableCell>
                  <TableCell>Device Name</TableCell>
                  <TableCell>Device Type</TableCell>
                  {!isBuildingSelected && <TableCell>Building Name</TableCell>}
                  <TableCell>Sensor Uptime</TableCell>
                  <TableCell>Controller Uptime</TableCell>
                  <TableCell>Sensor Last Record</TableCell>
                  <TableCell>Controller Last Record</TableCell>
                  <TableCell>Sensors Service Status</TableCell>
                  <TableCell>Controller Service Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devicesUptimeLoading ? (
                  <TableRow>
                    <TableCell colSpan={tableColumnCount} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text">
                        Loading devices uptime...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : devicesUptime.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tableColumnCount} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color={devicesUptimeError ? 'error' : 'text'}>
                        {emptyStateText}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  devicesUptime.map(device => {
                    const isSensorDevice = String(device.device_type || '').startsWith('Sensor');

                    return (
                      <TableRow key={device.device_id} hover>
                        <TableCell>{device.device_id || '-'}</TableCell>
                        <TableCell>{device.device_name || '-'}</TableCell>
                        <TableCell>{device.device_type || '-'}</TableCell>
                        {!isBuildingSelected && <TableCell>{device.building_name || '-'}</TableCell>}
                        <TableCell>{formatUptime(device.sensor_sum_records_count, rangeObj)}</TableCell>
                        <TableCell>{isSensorDevice ? '-' : formatUptime(device.controller_sum_records_count, rangeObj)}</TableCell>
                        <TableCell>{formatUnixTime(device.sensor_sum_last_record_time)}</TableCell>
                        <TableCell>{isSensorDevice ? '-' : formatUnixTime(device.controller_sum_last_record_time)}</TableCell>
                        <TableCell>
                          <ServiceStatusBadge status={getServiceStatus(device.sensor_sum_last_record_time)} />
                        </TableCell>
                        <TableCell>
                          {isSensorDevice ? (
                            '-'
                          ) : (
                            <ServiceStatusBadge status={getServiceStatus(device.controller_sum_last_record_time)} />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </MDBox>
    </DashboardLayout>
  );
};

export default Statistics;
